import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  type AdditionalInfoFeedbackItem,
  type AdditionalInfoFaqRow,
  type AdditionalInfoMode,
  type TemplateAdditionalInfoState,
  collectFeedbackItems,
  createEmptyFaqRow,
  createInitialFaqRows,
  FAQ_ANSWER_MAX,
  FAQ_ANSWER_MIN,
  faqRowErrorMessage,
  additionalInfoReplaceMessage,
  additionalInfoSectionHasContent,
  canAddFaqRow,
  faqRowsAreValid,
  INFO_MAX,
  isFaqRowComplete,
  isFaqRowStarted,
} from './additional-info.logic';

export type { AdditionalInfoFeedbackItem } from './additional-info.logic';

@Component({
  selector: 'lib-additional-info',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './additional-info.component.html',
  styleUrl: './additional-info.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdditionalInfoComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly faqAnswerMin = FAQ_ANSWER_MIN;
  readonly faqAnswerMax = FAQ_ANSWER_MAX;
  readonly infoMax = INFO_MAX;
  mode: AdditionalInfoMode | null = null;
  pendingMode: AdditionalInfoMode | null = null;
  faqRows: AdditionalInfoFaqRow[] = createInitialFaqRows();
  infoText = '';
  private faqTouched = false;

  replaceMessage(next: AdditionalInfoMode): string {
    return additionalInfoReplaceMessage(next);
  }

  selectMode(next: AdditionalInfoMode): void {
    if (this.mode === next) {
      this.pendingMode = null;
      this.mode = null;
      this.cdr.markForCheck();
      return;
    }
    if (
      this.mode &&
      additionalInfoSectionHasContent(this.mode, this.faqRows, this.infoText)
    ) {
      this.pendingMode = next;
      this.cdr.markForCheck();
      return;
    }
    this.applyMode(next);
  }

  confirmPendingMode(): void {
    if (!this.pendingMode) return;
    this.applyMode(this.pendingMode);
  }

  cancelPendingMode(): void {
    this.pendingMode = null;
    this.cdr.markForCheck();
  }

  private applyMode(next: AdditionalInfoMode): void {
    this.pendingMode = null;
    this.mode = next;
    if (next === 'info') {
      this.faqRows = createInitialFaqRows();
      this.faqTouched = false;
    } else {
      this.infoText = '';
      if (this.faqRows.length === 0) {
        this.faqRows = createInitialFaqRows();
      }
    }
    this.cdr.markForCheck();
  }

  get canAddMoreFaq(): boolean {
    return canAddFaqRow(this.faqRows);
  }

  addFaqRow(): void {
    if (!canAddFaqRow(this.faqRows)) {
      return;
    }
    this.faqRows = [...this.faqRows, createEmptyFaqRow()];
    this.cdr.markForCheck();
  }

  removeFaqRow(index: number): void {
    const next = this.faqRows.filter((_, i) => i !== index);
    if (next.length === 0) {
      this.mode = null;
      this.pendingMode = null;
      this.faqRows = createInitialFaqRows();
      this.faqTouched = false;
    } else {
      this.faqRows = next;
    }
    this.cdr.markForCheck();
  }

  closeInfo(): void {
    this.infoText = '';
    this.mode = null;
    this.pendingMode = null;
    this.cdr.markForCheck();
  }

  onFaqQuestionChange(index: number, value: string): void {
    const row = this.faqRows[index];
    if (!row) return;
    row.question = value;
    this.faqRows = [...this.faqRows];
    this.cdr.markForCheck();
  }

  onFaqAnswerChange(index: number, value: string): void {
    const row = this.faqRows[index];
    if (!row) return;
    row.answer = value.slice(0, FAQ_ANSWER_MAX);
    this.faqRows = [...this.faqRows];
    this.cdr.markForCheck();
  }

  onInfoTextChange(value: string): void {
    this.infoText = value.slice(0, INFO_MAX);
    this.cdr.markForCheck();
  }

  faqRowMessage(index: number): string {
    const row = this.faqRows[index];
    if (!row || !isFaqRowStarted(row)) return '';
    return faqRowErrorMessage(row);
  }

  showQuestionError(index: number): boolean {
    const row = this.faqRows[index];
    if (!row || !this.faqRowMessage(index)) return false;
    return row.question.trim().length === 0;
  }

  showAnswerError(index: number): boolean {
    const row = this.faqRows[index];
    if (!row || !this.faqRowMessage(index)) return false;
    return row.question.trim().length > 0;
  }

  validate(): boolean {
    this.faqTouched = true;
    this.cdr.markForCheck();
    if (this.mode !== 'faq') {
      return true;
    }
    return faqRowsAreValid(this.faqRows);
  }

  getFeedbackItems(): AdditionalInfoFeedbackItem[] {
    return collectFeedbackItems(this.mode, this.faqRows, this.infoText);
  }

  applyTemplate(state: TemplateAdditionalInfoState): void {
    this.mode = state.mode;
    this.faqRows =
      state.faqRows.length > 0
        ? state.faqRows.map((row) => ({
            question: row.question,
            answer: row.answer.slice(0, FAQ_ANSWER_MAX),
          }))
        : createInitialFaqRows();
    this.infoText = state.infoText.slice(0, INFO_MAX);
    this.pendingMode = null;
    this.faqTouched = false;
    this.cdr.markForCheck();
  }

  resetForm(): void {
    this.mode = null;
    this.pendingMode = null;
    this.faqRows = createInitialFaqRows();
    this.infoText = '';
    this.faqTouched = false;
    this.cdr.markForCheck();
  }
}
